/* eslint-disable @typescript-eslint/no-explicit-any -- Compatibility props mirror the permissive Recharts configuration API used by report pages. */
import React from 'react'
import { toKpiValueDisplay, toSentenceCase } from '@utils/textCase.util'
import {
  AreaChart as DesignSystemAreaChart,
  ChartFrame,
  GroupedVerticalBar,
  LineChart as DesignSystemLineChart,
  PieChart as DesignSystemPieChart,
  SingleVerticalBar,
  type BarChartBar,
  type LineChartSeries,
  type PieChartSlice,
} from '@enamelai/design-system'
import './ReportDesignSystemCharts.css'

type LooseProps = Record<string, any> & { children?: React.ReactNode }

type AxisConfigurationProps = LooseProps & {
  tick?:
    | React.ReactNode
    | Record<string, unknown>
    | ((props: any) => React.ReactNode)
  tickFormatter?: (value: number) => React.ReactNode
}

const formatChartLabel = (value: string): string =>
  toKpiValueDisplay(toSentenceCase(value))

/** Avoid repeating a month within a weekly range on compact trend axes. */
const formatTrendXAxisLabel = (value: unknown): string => {
  const label = String(value ?? '')
  const sameMonthRange = label.match(
    /^([A-Za-z]{3})\s+(\d{1,2})\s*([\-–—])\s*\1\s+(\d{1,2})$/i,
  )

  if (!sameMonthRange) return label

  const [, month, startDay, separator, endDay] = sameMonthRange
  return `${month} ${startDay}${separator}${endDay}`
}

const createConfigurationComponent = (
  displayName: string,
): React.FC<LooseProps> => {
  const Component: React.FC<LooseProps> = () => null
  Component.displayName = displayName
  return Component
}

/**
 * Recharts-shaped configuration components used by report pages. They do not
 * render DOM; ReportBarChart/ReportLineChart/ReportPieChart translate their
 * props into the corresponding Enamel DS chart primitives.
 */
export const Bar = createConfigurationComponent('ReportChartBar')
export const CartesianGrid = createConfigurationComponent(
  'ReportChartCartesianGrid',
)
export const Cell = createConfigurationComponent('ReportChartCell')
export const LabelList = createConfigurationComponent('ReportChartLabelList')
export const Legend = createConfigurationComponent('ReportChartLegend')
export const Line = createConfigurationComponent('ReportChartLine')
export const Pie = createConfigurationComponent('ReportChartPie')
export const Tooltip = createConfigurationComponent('ReportChartTooltip')
export const XAxis: React.FC<AxisConfigurationProps> =
  createConfigurationComponent('ReportChartXAxis')
export const YAxis: React.FC<AxisConfigurationProps> =
  createConfigurationComponent('ReportChartYAxis')

const elementsOfType = (
  children: React.ReactNode,
  type: React.ElementType,
): React.ReactElement<LooseProps>[] => {
  const matches: React.ReactElement<LooseProps>[] = []

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<LooseProps>(child)) return
    if (child.type === type) matches.push(child)
    if (child.props.children) {
      matches.push(...elementsOfType(child.props.children, type))
    }
  })

  return matches
}

const finiteNumber = (value: unknown): number => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

const formatAxisTick = (
  value: number,
  formatter: ((value: number) => React.ReactNode) | undefined,
): string => {
  const formatted = formatter ? formatter(value) : value
  return String(formatted ?? '')
}

const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)

const normalizeTooltipNumber = (value: number): number =>
  Number(value.toFixed(2))

const resolveTooltipFormatter = (
  children: React.ReactNode,
): ((value: number, name?: string, payload?: LooseProps) => string) => {
  const formatter = elementsOfType(children, Tooltip)[0]?.props.formatter

  return (value: number, name?: string, payload?: LooseProps): string => {
    const normalizedValue = normalizeTooltipNumber(value)
    if (typeof formatter !== 'function')
      return formatCompactNumber(normalizedValue)

    try {
      const formatted = formatter(normalizedValue, name, {
        payload,
        name,
        value: normalizedValue,
      })
      const displayValue = Array.isArray(formatted) ? formatted[0] : formatted
      return typeof displayValue === 'string' ||
        typeof displayValue === 'number'
        ? String(displayValue)
        : formatCompactNumber(normalizedValue)
    } catch {
      return formatCompactNumber(normalizedValue)
    }
  }
}

const resolveDomain = (
  domain: unknown,
  values: number[],
): { min: number; max: number } => {
  const fallbackMin = Math.min(0, ...values)
  const fallbackMax = Math.max(1, ...values)

  if (!Array.isArray(domain) || domain.length < 2) {
    return { min: fallbackMin, max: fallbackMax }
  }

  const min = Number(domain[0])
  const max = Number(domain[1])
  return {
    min: Number.isFinite(min) ? min : fallbackMin,
    max: Number.isFinite(max) && max > min ? max : fallbackMax,
  }
}

const defaultTicks = (min: number, max: number): number[] => {
  const step = (max - min) / 4
  return [max, max - step, max - step * 2, max - step * 3, min]
}

const normalizeValue = (value: number, min: number, max: number): number => {
  const range = Math.max(max - min, Number.EPSILON)
  return Math.min(100, Math.max(0, ((value - min) / range) * 100))
}

const resolveColor = (
  element: React.ReactElement<LooseProps>,
  index: number,
): string =>
  element.props.stroke ??
  element.props.fill ??
  ['var(--ds-blue-600)', 'var(--ds-blue-400)', 'var(--ds-cyan-500)'][index % 3]

const resolveLegend = (
  children: React.ReactNode,
  seriesElements: React.ReactElement<LooseProps>[],
): Array<{ label: string; color: string }> | undefined => {
  if (elementsOfType(children, Legend).length === 0) return undefined
  return seriesElements.map((element, index) => ({
    label: formatChartLabel(
      String(
        element.props.name ?? element.props.dataKey ?? `Series ${index + 1}`,
      ),
    ),
    color: resolveColor(element, index),
  }))
}

const resolvePlotHeight = (
  legend: Array<{ label: string; color: string }> | undefined,
): number => (legend ? 180 : 204)

export const ResponsiveContainer: React.FC<LooseProps> = ({
  children,
  width = '100%',
  height = '100%',
}) => (
  <div
    className="report-ds-chart__responsive"
    style={{ width, height } as React.CSSProperties}
  >
    {children}
  </div>
)

export const BarChart: React.FC<LooseProps> = ({ data = [], children }) => {
  const rows = Array.isArray(data) ? data : []
  const xAxis = elementsOfType(children, XAxis)[0]
  const yAxis = elementsOfType(children, YAxis).find((axis) => !axis.props.hide)
  const bars = elementsOfType(children, Bar)
  const xKey = xAxis?.props.dataKey ?? 'label'
  const values = bars.flatMap((bar) =>
    rows.map((row: LooseProps) => finiteNumber(row?.[bar.props.dataKey])),
  )
  const { min, max } = resolveDomain(yAxis?.props.domain, values)
  const configuredTicks = yAxis?.props.ticks
  const ticks = Array.isArray(configuredTicks)
    ? [...configuredTicks].map(finiteNumber).reverse()
    : defaultTicks(min, max)
  const tickFormatter = yAxis?.props.tickFormatter as
    | ((value: number) => React.ReactNode)
    | undefined
  const xLabels = rows.map((row: LooseProps) => String(row?.[xKey] ?? ''))
  const legend = resolveLegend(children, bars)
  const tooltipFormatter = resolveTooltipFormatter(children)
  // ChartFrame renders axis labels and the legend outside its plot height.
  // Reserve space for both so they remain inside the report section card.
  const plotHeight = resolvePlotHeight(legend)

  return (
    <div className="report-ds-chart report-ds-chart--bar">
      <ChartFrame
        plotHeight={plotHeight}
        xLabels={xLabels}
        yTicks={ticks.map((tick) => formatAxisTick(tick, tickFormatter))}
        showXAxisLabel={false}
        showYAxisLabel={false}
        legend={legend}
      >
        <div
          className="report-ds-chart__bars"
          style={{
            gridTemplateColumns: `repeat(${Math.max(rows.length, 1)}, minmax(0, 1fr))`,
          }}
        >
          {rows.map((row: LooseProps, rowIndex: number) => {
            const label = xLabels[rowIndex]
            if (bars.length <= 1) {
              const bar = bars[0]
              const actualValue = finiteNumber(row?.[bar?.props.dataKey])
              const cells = bar ? elementsOfType(bar.props.children, Cell) : []
              const color =
                cells[rowIndex]?.props.fill ??
                bar?.props.fill ??
                'var(--ds-blue-600)'
              const tooltipLabel = formatChartLabel(
                String(bar?.props.name ?? bar?.props.dataKey ?? 'Value'),
              )
              return (
                <SingleVerticalBar
                  key={`${label}-${rowIndex}`}
                  title={label}
                  hideTitle
                  height={plotHeight}
                  width={Math.min(72, finiteNumber(bar?.props.barSize) || 56)}
                  value={normalizeValue(actualValue, min, max)}
                  color={color}
                  showTooltip
                  tooltipLabel={tooltipLabel}
                  formatValue={() =>
                    tooltipFormatter(actualValue, tooltipLabel, row)
                  }
                  aria-label={`${label}: ${actualValue}`}
                />
              )
            }

            const groupedBars: BarChartBar[] = bars.map((bar, barIndex) => ({
              value: normalizeValue(
                finiteNumber(row?.[bar.props.dataKey]),
                min,
                max,
              ),
              color: resolveColor(bar, barIndex),
              label: formatChartLabel(
                String(
                  bar.props.name ??
                    bar.props.dataKey ??
                    `Series ${barIndex + 1}`,
                ),
              ),
            }))
            const accessibleValues = bars
              .map(
                (bar, barIndex) =>
                  `${groupedBars[barIndex].label} ${finiteNumber(
                    row?.[bar.props.dataKey],
                  )}`,
              )
              .join(', ')
            const formatGroupedValue = (normalizedValue: number): string => {
              const closestIndex = groupedBars.reduce(
                (closest, groupedBar, index) =>
                  Math.abs(groupedBar.value - normalizedValue) <
                  Math.abs(groupedBars[closest].value - normalizedValue)
                    ? index
                    : closest,
                0,
              )
              const sourceBar = bars[closestIndex]
              const actualValue = finiteNumber(row?.[sourceBar.props.dataKey])
              return tooltipFormatter(
                actualValue,
                groupedBars[closestIndex].label,
                row,
              )
            }

            return (
              <GroupedVerticalBar
                key={`${label}-${rowIndex}`}
                title={label}
                hideTitle
                height={plotHeight}
                bars={groupedBars}
                barWidth={Math.min(32, Math.max(12, 56 / bars.length))}
                showTooltip
                formatValue={formatGroupedValue}
                aria-label={`${label}: ${accessibleValues}`}
              />
            )
          })}
        </div>
      </ChartFrame>
    </div>
  )
}
export const LineChart: React.FC<LooseProps> = ({ data = [], children }) => {
  const rows = Array.isArray(data) ? data : []
  const xAxis = elementsOfType(children, XAxis)[0]
  const yAxis = elementsOfType(children, YAxis).find((axis) => !axis.props.hide)
  const lines = elementsOfType(children, Line)
  const xKey = xAxis?.props.dataKey ?? 'label'
  const values = lines.flatMap((line) =>
    rows.map((row: LooseProps) => finiteNumber(row?.[line.props.dataKey])),
  )
  const { min, max } = resolveDomain(yAxis?.props.domain, values)
  const configuredTicks = yAxis?.props.ticks
  const ticks = Array.isArray(configuredTicks)
    ? [...configuredTicks].map(finiteNumber).reverse()
    : defaultTicks(min, max)
  const tickFormatter = yAxis?.props.tickFormatter as
    | ((value: number) => React.ReactNode)
    | undefined
  const xLabels = rows.map((row: LooseProps) =>
    formatTrendXAxisLabel(row?.[xKey]),
  )
  const edgeLabelLength = Math.max(
    xLabels[0]?.length ?? 0,
    xLabels[xLabels.length - 1]?.length ?? 0,
  )
  const edgeLabelInset = Math.min(32, Math.max(8, edgeLabelLength * 3))
  const series: LineChartSeries[] = lines.map((line, index) => ({
    name: formatChartLabel(
      String(line.props.name ?? line.props.dataKey ?? `Series ${index + 1}`),
    ),
    color: resolveColor(line, index),
    strokeWidth: finiteNumber(line.props.strokeWidth) || 2,
    data: rows.map((row: LooseProps, rowIndex: number) => ({
      label: xLabels[rowIndex],
      value: finiteNumber(row?.[line.props.dataKey]),
    })),
  }))
  const legend = resolveLegend(children, lines)
  const tooltipFormatter = resolveTooltipFormatter(children)
  const plotHeight = resolvePlotHeight(legend)

  return (
    <div className="report-ds-chart report-ds-chart--line">
      <ChartFrame
        className="report-ds-chart__trend-frame"
        style={
          {
            '--report-chart-label-overhang': `${
              rows.length > 1 ? 50 / (rows.length - 1) : 0
            }%`,
            '--report-chart-label-offset': `${
              rows.length > 1 ? -50 / (rows.length - 1) : 0
            }%`,
            '--report-chart-edge-label-inset': `${edgeLabelInset}px`,
          } as React.CSSProperties
        }
        plotHeight={plotHeight}
        xLabels={xLabels}
        yTicks={ticks.map((tick) => formatAxisTick(tick, tickFormatter))}
        showXAxisLabel={false}
        showYAxisLabel={false}
        legend={legend}
      >
        {rows.length === 0 || series.length === 0 ? null : series.length ===
          1 ? (
          <DesignSystemAreaChart
            data={series[0].data}
            height={plotHeight}
            minValue={min}
            maxValue={max}
            strokeColor={series[0].color}
            strokeWidth={series[0].strokeWidth}
            seriesName={series[0].name}
            formatValue={(value) =>
              tooltipFormatter(
                value,
                series[0].name,
                rows.find(
                  (row: LooseProps) =>
                    finiteNumber(row?.[lines[0].props.dataKey]) === value,
                ),
              )
            }
            showTooltip
            aria-label={series[0].name}
          />
        ) : (
          <DesignSystemLineChart
            series={series}
            height={plotHeight}
            minValue={min}
            maxValue={max}
            showTooltip
            formatValue={(value) => tooltipFormatter(value)}
            aria-label={series.map((item) => item.name).join(', ')}
          />
        )}
      </ChartFrame>
    </div>
  )
}
export const PieChart: React.FC<LooseProps> = ({ children }) => {
  const pie = elementsOfType(children, Pie)[0]
  const rows = Array.isArray(pie?.props.data) ? pie.props.data : []
  const cells = pie ? elementsOfType(pie.props.children, Cell) : []
  const dataKey = pie?.props.dataKey ?? 'value'
  const nameKey = pie?.props.nameKey ?? 'name'
  const slices: PieChartSlice[] = rows.map(
    (row: LooseProps, index: number) => ({
      value: finiteNumber(row?.[dataKey]),
      label: formatChartLabel(String(row?.[nameKey] ?? `Segment ${index + 1}`)),
      color:
        cells[index]?.props.fill ??
        row?.color ??
        `var(--ds-blue-${Math.min(700, 300 + index * 100)})`,
    }),
  )
  const outerRadius = finiteNumber(pie?.props.outerRadius)
  const size = outerRadius > 0 ? outerRadius * 2 : 168

  return (
    <div className="report-ds-chart report-ds-chart--pie">
      <DesignSystemPieChart
        slices={slices}
        size={size}
        aria-label={slices
          .map((slice) => `${slice.label}: ${slice.value}`)
          .join(', ')}
      />
    </div>
  )
}
